import * as React from 'react'

import get from 'lodash/get'
import NextApp from 'next/app'
import getConfig from 'next/config'
import Head from 'next/head'

import { withSentry } from '@grandvision/analytics'
import { AuthProvider, Routes } from '@grandvision/auth'
import {
  BloomreachContextSettings,
  BloomreachProvider,
  BloomreachService,
  BrPageModel,
  getClientSession,
  getServerSession,
  RuntimeContext,
  Session,
} from '@grandvision/bloomreach'
import {
  GoogleMapsProvider,
  GoogleMapsSettings,
  MediaQuery,
  ToastContainer,
} from '@grandvision/common'
import { DistanceUnit } from '@grandvision/utils'
import { withApollo, withRedux } from '@grandvision/next'
import { DeliveryIndicatorProvider, StockIndicatorProvider } from '@grandvision/inventory'
import {
  ApiConfigProvider,
  Countries,
  Currencies,
  FeatureToggleProvider,
  IntlProvider,
  IntlSettings,
  Languages,
  mergeLabels,
  ReduxProvider,
  StoreWithPersistor,
} from '@grandvision/state'

import { isMultilingualEnabled, isMyAccountEnabled, isTabsEnabled } from '~/util/feature-toggle'
import ErrorPage from './_error'

import '../styles/index.scss'

import { componentDefinitions, customComponents } from '~/cms'

export interface NextProcess extends NodeJS.Process {
  browser: boolean
}

const { publicRuntimeConfig } = getConfig()
const bloomreachSettings = publicRuntimeConfig?.bloomreach ?? {}
const defaultLocale = publicRuntimeConfig?.defaultLocale ?? 'en-IO'
const {
  grandvision: {
    apiUrl,
    endpoints,
    features,
    myAccount: { auth0Domain, auth0ClientId, auth0CustomDomain },
  },
} = publicRuntimeConfig

const cmsService = BloomreachService(isMultilingualEnabled())

const routes: Routes = {
  createAccount: 'create',
  login: 'login',
  logout: 'logout',
  orders: 'orders',
  signup: 'signup',
  profile: 'profile',
  addressBook: 'address-book',
  resetPassword: 'reset-password',
  linkExpired: 'link-expired',
  setPassword: 'set-password',
  changePassword: 'change-password',
  unlinkOrder: 'unlink-order',
  unlinkAppointment: 'unlink-appointment',
}

const authSettings = {
  apiUrl,
  routes,
  basePath: 'my-account',
  clientId: auth0ClientId,
  customDomain: auth0CustomDomain,
  domain: auth0Domain,
  checkoutRoute: '/checkout/shipping',
  enableLegacyOrderHistory: isTabsEnabled(),
}

const apiConfig = {
  apiUrl,
  productsUrl: endpoints.products,
}

const googleMapsSettings: GoogleMapsSettings = {
  apiKey: publicRuntimeConfig.googleMaps.apiKey,
  defaultCenter: publicRuntimeConfig.googleMaps.mapCenter,
  defaultZoom: 11,
  pin: '/img/pin.png',
}

type Props = {
  pageProps: any
  pageModel?: BrPageModel
  preview: boolean
  store: StoreWithPersistor
  labels: Record<string, Record<string, string>>
  errorFlag: boolean
  initialBreakpoint: MediaQuery.BreakpointName
  locale: string
  path?: string
  visitorId?: string
}

class App extends NextApp<Props> {
  static async getInitialProps({ Component, ctx }) {
    const nextProcess = process as NextProcess
    const session: Session =
      nextProcess && nextProcess.browser ? getClientSession({}) : getServerSession(ctx.req)

    cmsService.setCmsUrls(publicRuntimeConfig.bloomreach.cmsUrls)

    const preview = cmsService.isCmsPreview(session.search)
    const query: any = (ctx || {}).query || {}
    const locale: string = ctx.req?.locale?.toString() ?? defaultLocale
    const path = ctx.req?.originalUrl ?? '' // path + query params
    const request = {
      hostname: session.host,
      path,
    }

    let pageModel: BrPageModel | undefined = query.pageModel

    let errorFlag = false
    let data = ctx

    if (!pageModel) {
      try {
        pageModel = await cmsService.fetchCmsPage(request, preview, locale, session.cookie)
        const pageMeta = cmsService.getPageMeta(pageModel)

        data = {
          ...ctx,
          query: {
            search: query,
            request,
            preview,
            pageModel,
            locale,
            ...pageMeta,
          },
        }
      } catch (err) {
        // tslint:disable-next-line no-console
        console.error(err)
        errorFlag = true
      }
    }

    const tasks: Promise<any>[] = []

    // If we still don't have a pageModel (or have an error) by now, we should assume
    // bloomreach is not working as expected and don't try to fetch labels
    if (!errorFlag && pageModel) {
      tasks.push(
        cmsService.fetchCmsResource(
          bloomreachSettings.cmsUrls.live,
          bloomreachSettings.channel,
          ['*'], // the "*" fetches all available label resources
          locale
        )
      )
    }

    if (Component.getInitialProps && typeof Component.getInitialProps === 'function') {
      tasks.push(Component.getInitialProps.call(null, data))
    }

    const [labels, pageProps] = await Promise.all(tasks)

    return {
      locale,
      errorFlag,
      pageModel,
      preview,
      path,
      pageProps: pageProps || {},
      labels: labels || {},
      initialBreakpoint: ctx.req.mediaQueryBreakpoint,
      visitorId: query.bloomreachCmsSettings?.visitorId,
    }
  }

  render() {
    const {
      Component,
      pageProps,
      store,
      labels,
      errorFlag,
      locale,
      initialBreakpoint,
      pageModel,
      preview,
      path,
      visitorId,
    } = this.props

    if (errorFlag) {
      return <ErrorPage statusCode={500} />
    }

    const mergedLabels = mergeLabels(labels as any)
    const [language, country] = locale.split('-')
    const currency = get(publicRuntimeConfig, 'currency', Currencies.EUR)
    const distanceUnit = DistanceUnit.km
    const intlSettings: IntlSettings = {
      language: Languages[language],
      country: Countries[country],
      currency,
      distanceUnit,
    }

    // omit `bloomreach` from `publicRuntimeConfig`
    const { bloomreach, deliveryIndicator, stockIndicator, ...cleanPublicRuntimeConfig } =
      publicRuntimeConfig

    const bloomreachContextSettings: BloomreachContextSettings = {
      page: { toJSON: () => pageModel, isPreview: () => preview },
      cmsUrls: bloomreachSettings.cmsUrls,
      excludeSkuFromProductUrls: true,
      customComponentMap: customComponents,
      componentDefinitions,
      visitorId,
      path,
    }

    return (
      <React.StrictMode>
        <Head>
          {/*https://github.com/vercel/next.js/blob/master/errors/no-document-viewport-meta.md*/}
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Head>
        <BloomreachProvider settings={bloomreachContextSettings}>
          <ReduxProvider store={store}>
            <FeatureToggleProvider enabledFeatures={features}>
              <IntlProvider settings={intlSettings} labels={mergedLabels}>
                <ApiConfigProvider apiConfig={apiConfig}>
                  <GoogleMapsProvider settings={googleMapsSettings}>
                    <StockIndicatorProvider settings={stockIndicator}>
                      <DeliveryIndicatorProvider settings={deliveryIndicator}>
                        <MediaQuery.Provider initialBreakpoint={initialBreakpoint}>
                          <AuthProvider
                            enabled={isMyAccountEnabled()}
                            settings={{ ...authSettings }}
                          >
                            <RuntimeContext.Provider
                              value={{
                                ...cleanPublicRuntimeConfig,
                                headComponent: Head,
                              }}
                            >
                              <ToastContainer />
                              <Component {...pageProps} labels={labels} store={store} />
                            </RuntimeContext.Provider>
                          </AuthProvider>
                        </MediaQuery.Provider>
                      </DeliveryIndicatorProvider>
                    </StockIndicatorProvider>
                  </GoogleMapsProvider>
                </ApiConfigProvider>
              </IntlProvider>
            </FeatureToggleProvider>
          </ReduxProvider>
        </BloomreachProvider>
      </React.StrictMode>
    )
  }
}

export default withSentry(withRedux(withApollo()(App)))
